import { useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { ArrowDownLeft, ArrowUpRight, Trash } from "@phosphor-icons/react";
import { Button } from "@/components/ui/button";
import { PageHeader, Kpi, Th, Td, Empty, Tag, Segmented, DateRange, Confirm } from "@/components/common";
import PaymentDialog from "@/components/PaymentDialog";
import { api, money, fmtDate, L, errMsg, monthStart, todayStr } from "@/lib/api";
import { useLoad, useQueryParam } from "@/lib/useLoad";

export default function Payments() {
  const [kind, setKind] = useState("");
  useQueryParam("tur", setKind);
  const [start, setStart] = useState(monthStart());
  const [end, setEnd] = useState(todayStr());
  const [dlg, setDlg] = useState(null);
  const [rows, reload] = useLoad(() => api.get("/payments", { params: { kind: kind || undefined, start, end } }).then((r) => r.data), [kind, start, end]);
  const list = rows || [];
  const tin = list.filter((p) => p.kind === "collection").reduce((s, p) => s + p.amount_try, 0);
  const tout = list.filter((p) => p.kind === "payment").reduce((s, p) => s + p.amount_try, 0);
  const del = async (id) => {
    try {
      await api.delete(`/payments/${id}`);
      toast.success("Kayıt silindi");
      reload();
    } catch (e) {
      toast.error(errMsg(e));
    }
  };

  return (
    <div>
      <PageHeader eyebrow="Tahsilat & Ödemeler" title="Kasa">
        <Button variant="outline" className="rounded-[4px]" onClick={() => setDlg("payment")} data-testid="new-payment-button"><ArrowUpRight /> Ödeme Yap</Button>
        <Button className="rounded-[4px]" onClick={() => setDlg("collection")} data-testid="new-collection-button"><ArrowDownLeft /> Tahsilat Al</Button>
      </PageHeader>
      <div className="mb-6 grid grid-cols-1 gap-4 sm:grid-cols-3">
        <Kpi label="Toplam Tahsilat" value={money(tin)} tone="green" testid="cash-total-in" />
        <Kpi label="Toplam Ödeme" value={money(tout)} tone="red" testid="cash-total-out" delay={40} />
        <Kpi label="Net Kasa Akışı" value={money(tin - tout)} tone={tin - tout >= 0 ? "blue" : "red"} testid="cash-net" delay={80} />
      </div>
      <div className="panel">
        <div className="flex flex-col gap-3 border-b border-border p-4 md:flex-row md:items-center md:justify-between">
          <Segmented value={kind} onChange={setKind} prefix="cash-filter" options={[["", "Tümü"], ["collection", "Tahsilat"], ["payment", "Ödeme"]]} />
          <DateRange start={start} end={end} onStart={setStart} onEnd={setEnd} prefix="cash" />
        </div>
        <div className="overflow-x-auto">
          <table className="w-full" data-testid="payments-table">
            <thead><tr><Th>No</Th><Th>Tarih</Th><Th>Tür</Th><Th>Cari</Th><Th>Yöntem</Th><Th>Açıklama</Th><Th right>Tutar</Th><Th right>TL Karşılığı</Th><Th /></tr></thead>
            <tbody>
              {rows && list.length === 0 && <Empty cols={9} />}
              {list.map((p) => (
                <tr key={p.id} className="hover:bg-muted/40" data-testid={`payment-row-${p.no}`}>
                  <Td className="font-num text-xs font-semibold">{p.no}</Td>
                  <Td className="font-num text-xs">{fmtDate(p.date)}</Td>
                  <Td><Tag tone={p.kind}>{L.payKind[p.kind]}</Tag></Td>
                  <Td><Link to={`/cariler/${p.account_id}`} className="font-medium hover:text-primary">{p.account_name}</Link></Td>
                  <Td className="text-muted-foreground">{L.method[p.method]}</Td>
                  <Td className="text-muted-foreground">{p.description || "—"}</Td>
                  <Td right className={p.kind === "collection" ? "text-emerald-700" : "text-red-600"}>{money(p.amount, p.currency)}</Td>
                  <Td right className="text-muted-foreground">{money(p.amount_try)}</Td>
                  <Td>
                    <Confirm title="Kayıt silinsin mi?" description="Cari bakiye bu işlem kadar geri alınacak." onConfirm={() => del(p.id)} testid={`payment-delete-confirm-${p.no}`}
                      trigger={<Button variant="ghost" size="icon" className="text-red-600" data-testid={`payment-delete-button-${p.no}`}><Trash /></Button>} />
                  </Td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
      <PaymentDialog open={!!dlg} onOpenChange={(o) => !o && setDlg(null)} kind={dlg || "collection"} onSaved={reload} />
    </div>
  );
}
